import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { firstValueFrom } from 'rxjs';
import {
    CashRegister,
    CashSession,
    CashMovement,
    CashTransfer,
    CashSessionSummary,
    PettyCashAccount,
    PettyCashDirection
} from '../models/cash-register.model';

@Injectable({
    providedIn: 'root'
})
export class CashRegisterService {
    private http = inject(HttpClient);

    private readonly baseUrl = environment.apiUrls.partners;
    private readonly registersUrl = `${this.baseUrl}/cash-registers`;
    private readonly sessionsUrl = `${this.baseUrl}/cash-sessions`;
    private readonly transfersUrl = `${this.baseUrl}/cash-transfers`;

    // ─── State ───
    readonly registers = signal<CashRegister[]>([]);
    readonly activeSession = signal<CashSession | null>(null);
    readonly isLoading = signal(false);
    readonly error = signal<string | null>(null);

    // ─── Cash Registers ───

    async getAll(): Promise<CashRegister[]> {
        this.isLoading.set(true);
        this.error.set(null);
        try {
            const result = await firstValueFrom(
                this.http.get<CashRegister[]>(this.registersUrl)
            );
            this.registers.set(result);
            return result;
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to load cash registers';
            console.error('[CashRegisterService] getAll failed:', err);
            this.error.set(message);
            throw err;
        } finally {
            this.isLoading.set(false);
        }
    }

    async getById(id: string): Promise<CashRegister> {
        this.isLoading.set(true);
        this.error.set(null);
        try {
            return await firstValueFrom(
                this.http.get<CashRegister>(`${this.registersUrl}/${id}`)
            );
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to load cash register';
            console.error('[CashRegisterService] getById failed:', err);
            this.error.set(message);
            throw err;
        } finally {
            this.isLoading.set(false);
        }
    }

    async create(payload: Omit<CashRegister, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>): Promise<CashRegister> {
        this.isLoading.set(true);
        this.error.set(null);
        try {
            const result = await firstValueFrom(
                this.http.post<CashRegister>(this.registersUrl, payload)
            );
            this.registers.update(list => [...list, result]);
            return result;
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to create cash register';
            console.error('[CashRegisterService] create failed:', err);
            this.error.set(message);
            throw err;
        } finally {
            this.isLoading.set(false);
        }
    }

    async update(id: string, payload: Partial<CashRegister>): Promise<CashRegister> {
        this.isLoading.set(true);
        this.error.set(null);
        try {
            const result = await firstValueFrom(
                this.http.patch<CashRegister>(`${this.registersUrl}/${id}`, payload)
            );
            this.registers.update(list => list.map(r => r.id === id ? result : r));
            return result;
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to update cash register';
            console.error('[CashRegisterService] update failed:', err);
            this.error.set(message);
            throw err;
        } finally {
            this.isLoading.set(false);
        }
    }

    async delete(id: string): Promise<void> {
        this.isLoading.set(true);
        this.error.set(null);
        try {
            await firstValueFrom(
                this.http.delete<void>(`${this.registersUrl}/${id}`)
            );
            this.registers.update(list => list.filter(r => r.id !== id));
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to delete cash register';
            console.error('[CashRegisterService] delete failed:', err);
            this.error.set(message);
            throw err;
        } finally {
            this.isLoading.set(false);
        }
    }

    // ─── Cash Sessions ───

    async openSession(payload: {
        cashRegisterId: number;
        openingAmount: number;
        currency: string;
    }): Promise<CashSession> {
        this.isLoading.set(true);
        this.error.set(null);
        try {
            const result = await firstValueFrom(
                this.http.post<CashSession>(`${this.sessionsUrl}/open`, payload)
            );
            this.activeSession.set(result);
            return result;
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to open cash session';
            console.error('[CashRegisterService] openSession failed:', err);
            this.error.set(message);
            throw err;
        } finally {
            this.isLoading.set(false);
        }
    }

    async getActiveSession(): Promise<CashSession | null> {
        this.isLoading.set(true);
        this.error.set(null);
        try {
            const result = await firstValueFrom(
                this.http.get<CashSession | null>(`${this.sessionsUrl}/active`)
            );
            this.activeSession.set(result);
            return result;
        } catch (err: unknown) {
            // 404 means no active session — not an error
            if ((err as { status?: number }).status === 404) {
                this.activeSession.set(null);
                return null;
            }
            const message = err instanceof Error ? err.message : 'Failed to load active session';
            console.error('[CashRegisterService] getActiveSession failed:', err);
            this.error.set(message);
            throw err;
        } finally {
            this.isLoading.set(false);
        }
    }

    async getSession(id: string): Promise<CashSession> {
        this.isLoading.set(true);
        this.error.set(null);
        try {
            return await firstValueFrom(
                this.http.get<CashSession>(`${this.sessionsUrl}/${id}`)
            );
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to load session';
            console.error('[CashRegisterService] getSession failed:', err);
            this.error.set(message);
            throw err;
        } finally {
            this.isLoading.set(false);
        }
    }

    async getSessions(cashRegisterId?: string): Promise<CashSession[]> {
        this.isLoading.set(true);
        this.error.set(null);
        try {
            const url = cashRegisterId
                ? `${this.sessionsUrl}?cashRegisterId=${cashRegisterId}`
                : this.sessionsUrl;
            return await firstValueFrom(
                this.http.get<CashSession[]>(url)
            );
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to load sessions';
            console.error('[CashRegisterService] getSessions failed:', err);
            this.error.set(message);
            throw err;
        } finally {
            this.isLoading.set(false);
        }
    }

    async closeSession(id: string, payload: {
        actualClosingAmount: number;
        closingNotes?: string;
    }): Promise<CashSession> {
        this.isLoading.set(true);
        this.error.set(null);
        try {
            const result = await firstValueFrom(
                this.http.post<CashSession>(`${this.sessionsUrl}/${id}/close`, payload)
            );
            if (this.activeSession()?.id === id) {
                this.activeSession.set(null);
            }
            return result;
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to close session';
            console.error('[CashRegisterService] closeSession failed:', err);
            this.error.set(message);
            throw err;
        } finally {
            this.isLoading.set(false);
        }
    }

    async getSessionSummary(id: string): Promise<CashSessionSummary> {
        this.isLoading.set(true);
        this.error.set(null);
        try {
            return await firstValueFrom(
                this.http.get<CashSessionSummary>(`${this.sessionsUrl}/${id}/summary`)
            );
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to load session summary';
            console.error('[CashRegisterService] getSessionSummary failed:', err);
            this.error.set(message);
            throw err;
        } finally {
            this.isLoading.set(false);
        }
    }

    // ─── Movements ───

    async getMovements(sessionId: string): Promise<CashMovement[]> {
        this.isLoading.set(true);
        this.error.set(null);
        try {
            return await firstValueFrom(
                this.http.get<CashMovement[]>(`${this.sessionsUrl}/${sessionId}/movements`)
            );
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to load movements';
            console.error('[CashRegisterService] getMovements failed:', err);
            this.error.set(message);
            throw err;
        } finally {
            this.isLoading.set(false);
        }
    }

    async addMovement(sessionId: string, payload: {
        type: CashMovement['type'];
        amount: number;
        description?: string;
        /** Compte du plan comptable choisi : il determine l'ecriture comptable du mouvement. */
        accountId: number;
    }): Promise<CashMovement> {
        this.isLoading.set(true);
        this.error.set(null);
        try {
            return await firstValueFrom(
                this.http.post<CashMovement>(`${this.sessionsUrl}/${sessionId}/movements`, payload)
            );
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to add movement';
            console.error('[CashRegisterService] addMovement failed:', err);
            this.error.set(message);
            throw err;
        } finally {
            this.isLoading.set(false);
        }
    }

    /** Comptes du plan comptable que l'entreprise a rendus proposables a la petite caisse pour ce sens. */
    async getPettyCashAccounts(direction: PettyCashDirection): Promise<PettyCashAccount[]> {
        try {
            return await firstValueFrom(
                this.http.get<PettyCashAccount[]>(`${this.sessionsUrl}/petty-cash-accounts`, { params: { direction } })
            );
        } catch (err: unknown) {
            console.error('[CashRegisterService] getPettyCashAccounts failed:', err);
            throw err;
        }
    }

    // ─── Transfers ───

    async createTransfer(payload: {
        fromSessionId: number;
        toSessionId: number;
        amount: number;
        currency: string;
        reason?: string;
    }): Promise<CashTransfer> {
        this.isLoading.set(true);
        this.error.set(null);
        try {
            return await firstValueFrom(
                this.http.post<CashTransfer>(this.transfersUrl, payload)
            );
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to create transfer';
            console.error('[CashRegisterService] createTransfer failed:', err);
            this.error.set(message);
            throw err;
        } finally {
            this.isLoading.set(false);
        }
    }

    async acceptTransfer(id: string): Promise<CashTransfer> {
        this.isLoading.set(true);
        this.error.set(null);
        try {
            return await firstValueFrom(
                this.http.patch<CashTransfer>(`${this.transfersUrl}/${id}/accept`, {})
            );
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to accept transfer';
            console.error('[CashRegisterService] acceptTransfer failed:', err);
            this.error.set(message);
            throw err;
        } finally {
            this.isLoading.set(false);
        }
    }

    async rejectTransfer(id: string, reason?: string): Promise<CashTransfer> {
        this.isLoading.set(true);
        this.error.set(null);
        try {
            return await firstValueFrom(
                this.http.patch<CashTransfer>(`${this.transfersUrl}/${id}/reject`, { reason })
            );
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to reject transfer';
            console.error('[CashRegisterService] rejectTransfer failed:', err);
            this.error.set(message);
            throw err;
        } finally {
            this.isLoading.set(false);
        }
    }

    async getPendingTransfers(): Promise<CashTransfer[]> {
        this.isLoading.set(true);
        this.error.set(null);
        try {
            return await firstValueFrom(
                this.http.get<CashTransfer[]>(`${this.transfersUrl}/pending`)
            );
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to load pending transfers';
            console.error('[CashRegisterService] getPendingTransfers failed:', err);
            this.error.set(message);
            throw err;
        } finally {
            this.isLoading.set(false);
        }
    }
}
